import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { listPrograms, listSessions } from "../apis/programsApi";
import { 
  Box, 
  Button, 
  Card, 
  CardContent, 
  CardMedia, 
  Grid, 
  Stack, 
  Typography,
  Chip,
  Divider,
  Container,
  Skeleton,
  DialogActions,
  Dialog,
  DialogContent,
  DialogTitle
} from "@mui/material";
import { 
  AccessTime, 
  AttachMoney, 
  Person, 
  People,
  ArrowBack 
} from "@mui/icons-material";
import { useAuth } from "../context/AuthContext";

const ProgramDetails = () => {
  const { programId } = useParams();
  const navigate = useNavigate();
  const [program, setProgram] = useState<any | null>(null);
  const [sessions, setSessions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [popupMessage] = useState("Please log in to register for this session.");
  const [open, setOpen] = useState(false);
  const { isLoggedIn } = useAuth()

  useEffect(() => {
    (async () => {
      try {
        const programs = await listPrograms();
        const filteredPrograms = programs.filter((p: any) => p.isActive);
        const found = filteredPrograms.find((p: any) => p.id === programId);
        setProgram(found || null);
        if (programId) {
          const s = await listSessions(programId);
          const filteredSessions = s.filter((s: any) => s.isActive);
          setSessions(filteredSessions || []);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [programId]);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2, mb: 3 }} />
        <Skeleton variant="text" sx={{ fontSize: '2rem', mb: 2 }} />
        <Skeleton variant="text" sx={{ fontSize: '1rem', mb: 4 }} />
        <Grid container spacing={2}>
          {[1, 2, 3].map((i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Skeleton variant="rectangular" height={250} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      </Container>
    );
  }

  if (!program) {
    return (
      <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h4" gutterBottom color="text.secondary">
          Program Not Found
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          The program you're looking for doesn't exist or has been removed.
        </Typography>
        {/* <Button 
          variant="contained" 
          startIcon={<ArrowBack />}
          onClick={() => navigate('/programs')}
        >
          Back to Programs
        </Button> */}
      </Container>
    );
  }

  return (
    <Box sx={{ bgcolor: '#f8f9fa', minHeight: '100vh', py: 4 }}>
      <Container maxWidth="lg">
        {/* Back Button */}
        {/* <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/programs')}
          sx={{ mb: 3, textTransform: 'none', color: 'text.secondary' }}
        >
          Back to Programs
        </Button> */}

        {/* Program Header */}
        <Card sx={{ borderRadius: 3, overflow: 'hidden', mb: 4, boxShadow: 3 }}>
          <Grid container>
            {program.imageUrl && (
              <Grid item xs={12} md={5}>
                <CardMedia
                  component="img"
                  src={program.imageUrl}
                  alt={program.title}
                  sx={{ 
                    height: { xs: 250, md: '300px' }, 
                    // minHeight: 300,
                    objectFit: 'cover' 
                  }}
                />
              </Grid>
            )}
            <Grid item xs={12} md={program.imageUrl ? 7 : 12}>
              <CardContent sx={{ p: { xs: 3, md: 4 }, height: '100%' }}>
                <Typography 
                  variant="h3" 
                  fontWeight={700} 
                  gutterBottom
                  sx={{ fontSize: { xs: '2rem', md: '2.5rem' } }}
                >
                  {program.title}
                </Typography>
                
                {program.description && (
                  <Typography 
                    variant="body1" 
                    color="text.secondary" 
                    sx={{ mb: 3, lineHeight: 1.7 }}
                  >
                    {program.description}
                  </Typography>
                )}

                <Divider sx={{ my: 3 }} />

                {/* Program Info Chips */}
                <Stack direction="row" spacing={2} flexWrap="wrap" gap={1.5}>
                  {program.price && (
                    <Chip
                      icon={<AttachMoney />}
                      label={`$${program.price}`}
                      color="primary"
                      variant="outlined"
                      sx={{ fontWeight: 600 }}
                    />
                  )}
                  {program.duration && (
                    <Chip
                      icon={<AccessTime />}
                      label={program.duration}
                      color="default"
                      variant="outlined"
                      sx={{ fontWeight: 600 }}
                    />
                  )}
                  {program?.programCoach && (
                    <Chip
                      icon={<Person />}
                      label={`Program Coach: ${program.programCoach.firstName} ${program.programCoach.lastName}` || program.programCoach.email}
                      color="secondary"
                      variant="outlined"
                      sx={{ fontWeight: 600 }}
                    />
                  )}
                </Stack>
              </CardContent>
            </Grid>
          </Grid>
        </Card>

        {/* Sessions Section */}
        <Box>
          <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h4" fontWeight={700}>
              Available Sessions
            </Typography>
            <Chip 
              label={sessions.length} 
              color="primary" 
              size="small"
              sx={{ fontWeight: 600 }}
            />
          </Box>

          {sessions.length === 0 ? (
            <Card sx={{ borderRadius: 3, p: 4, textAlign: 'center', boxShadow: 2 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Sessions Available Yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Check back soon for upcoming sessions for this program.
              </Typography>
            </Card>
          ) : (
            <Grid container spacing={3}>
              {sessions.map((s: any) => (
                <Grid item xs={12} sm={6} md={4} key={s.id}>
                  <Card 
                    sx={{ 
                      borderRadius: 3, 
                      height: '100%',
                      transition: 'all 0.3s ease',
                      boxShadow: 2,
                      '&:hover': {
                        boxShadow: 6,
                        transform: 'translateY(-4px)'
                      }
                    }}
                  >
                    {s.imageUrl && (
                      <CardMedia
                        component="img"
                        src={s.imageUrl}
                        alt={s.name}
                        sx={{ height: 180, objectFit: 'cover' }}
                      />
                    )}
                    <CardContent sx={{ p: 3 }}>
                      <Typography 
                        variant="h6" 
                        fontWeight={700} 
                        gutterBottom
                        sx={{ mb: 2 }}
                      >
                        {s.name}
                      </Typography>

                       <Stack spacing={1.5} sx={{ mb: 3 }}>
                         {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                           <People sx={{ fontSize: 20, color: 'text.secondary' }} />
                           {(() => {
                             const capacity = Number(s.capacity ?? 0);
                             const enrolled = Number(s.enrolledCount ?? 0);
                             const spotsLeft = Math.max(0, capacity - enrolled);
                             const isFull = capacity > 0 && spotsLeft === 0;
                             return (
                               <Typography variant="body2" color={isFull ? 'error.main' : 'text.secondary'}>
                                 {isFull ? (
                                   <><strong>Session Full</strong></>
                                 ) : (
                                   <>Capacity: <strong>{capacity}</strong> {capacity ? `• ${spotsLeft} spot${spotsLeft === 1 ? '' : 's'} left` : ''}</>
                                 )}
                               </Typography>
                             );
                           })()}
                         </Box> */}

                        {Array.isArray(s.ageGroups) && s.ageGroups.length > 0 && (
                          <Box>
                            <Typography 
                              variant="caption" 
                              color="text.secondary" 
                              sx={{ display: 'block', mb: 0.5 }}
                            >
                              Age Groups:
                            </Typography>
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                              {s.ageGroups.map((age: string, idx: number) => (
                                <Chip
                                  key={idx}
                                  label={age}
                                  size="small"
                                  sx={{ fontSize: '0.75rem' }}
                                />
                              ))}
                            </Stack>
                          </Box>
                        )}
                      </Stack>

                       <Button
                         variant="contained"
                         color="primary"
                         fullWidth
                         size="large"
                         onClick={() => {
                          if (isLoggedIn) {
                            navigate(
                              `/programs?programId=${program.id}&sessionId=${s.id}`,
                              {
                                state: { programId: program.id, sessionId: s.id },
                                replace: false,
                              }
                            );
                          } else {
                            setOpen(true);
                          }
                        }}
                         sx={{ 
                           textTransform: 'none',
                           fontWeight: 600,
                           borderRadius: 2,
                           py: 1.5
                         }}
                       >
                         Register Now
                       </Button>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
            
          )}
          
        </Box>
        <Dialog open={open} onClose={() => {
          setOpen(false);
        }}>
        <DialogTitle>Register for Session</DialogTitle>
        <DialogContent>
          <Typography>{popupMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpen(false);
            if (popupMessage.includes("log in")) {
              navigate(`/login`);
            }
          }} color="primary">
            OK
          </Button>
        </DialogActions>
      </Dialog>
      </Container>
    </Box>
  );
};

export default ProgramDetails;